/**
 * MapClientSearch: the client search both maps carry, first in each map's
 * toolbar, so the Team map and the Directory map cannot drift.
 *
 * The field is the house SearchField. It matches by name (matchMapClients)
 * over the candidates the caller hands in: the rows of the map's own
 * client read, already narrowed to what that map draws or lists. Typing
 * sends nothing; nothing else is searched.
 *
 * The results list sits under the field, the field's width, over the map,
 * and shows while the field holds text and focus is in the search (the
 * field or the list). Each row is a button named by the client's name,
 * first letter capitalised, with the client's facts on labelled lines (a
 * `dl` the button's aria-describedby names): Care manager, Unit and, for a
 * pin placed by town or ZIP, Location; for a client not on the map, one
 * line, the reason. A client with no address on file is listed too
 * (LP-42), greyed in the house's muted text tone (text-ink-soft, the name
 * at the regular weight) and still a button like every other row. Names and values wrap;
 * nothing is cut. More matches than the list shows are counted on their
 * own line. No match reads plain lines (LP-43): check the spelling, the
 * map shows active clients and only records you can open, and, only while
 * a Unit or Care manager filter is set, clear it to search every client.
 *
 * Keys and focus: a pointer press on a result keeps focus in the field;
 * Escape with text empties the field (SearchField's rule), which closes
 * the list; focus leaving the search closes the list and keeps the text,
 * and focusing the field again reopens it; a choice clears the field and
 * returns focus to it. While the list shows, the field names it through
 * aria-controls. The field stays a search box: no aria-expanded, no arrow
 * keys, and no spoken count.
 */

import { useId, useMemo, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';
import { capitalizeFirst } from '@/lib/capitalizeFirst';
import type { ClientMapNotPlacedWire, ClientMapPinWire } from '@/lib/capacityApi';
import { clientNotPlacedLabel } from '@/lib/clientLocation';
import { MAP_CLIENT_SEARCH_LIMIT, matchMapClients } from '@/lib/mapClientSearch';
import type { WorkingState } from '@/lib/tenantConfig';

export interface MapClientSearchProps {
  /** Placed candidates, already narrowed by the caller. */
  pins: readonly ClientMapPinWire[];
  /** Not-placed candidates, already narrowed by the caller. */
  notPlaced: readonly ClientMapNotPlacedWire[];
  workingStates: readonly WorkingState[];
  /** The client read is pending. */
  loading: boolean;
  /** The client read failed. */
  error: boolean;
  /** A Unit or Care manager filter is set on the map. */
  filtersActive: boolean;
  onPick: (clientId: string) => void;
  /** The field took focus. */
  onFocus?: () => void;
  /** The field fills its line at every width (a phone bar's own row),
   *  instead of narrowing to 18rem from sm up. */
  fullWidth?: boolean;
}

/** The house label role: uppercase, tracked, muted. */
const FACT_LABEL_CLS = 'text-label tracking-label font-semibold uppercase text-sage-400';
const FACT_VALUE_CLS = 'min-w-0 break-words text-xs text-ink';
const FACT_LIST_CLS = 'grid grid-cols-[max-content_minmax(0,1fr)] items-baseline gap-x-2 gap-y-0.5';
/** A row the map cannot draw at all (no address on file) is greyed in the
 *  house's muted text tone, text-ink-soft, so it stands back and still
 *  reads at AA (about 7:1 on paper; the archived rows' opacity-60 would
 *  put the label near 2.3:1). The name drops to the regular weight. */
const MUTED_NAME_CLS = 'font-normal text-ink-soft';
const MUTED_FACTS_CLS = '[&_dd]:text-ink-soft';

/** LP-43: no match, in plain lines. The filter line shows only while a
 *  Unit or Care manager filter is set (the Directory map has neither). */
export const MAP_SEARCH_NO_MATCH_LINES = [
  'No one by that name. Check the spelling.',
  'The map shows active clients only, and only the records you can open.',
] as const;
export const MAP_SEARCH_FILTER_LINE = 'To search every client, clear the Unit or Care manager filter.';

interface Fact {
  label: string;
  value: string;
}

function FactList({ id, testId, facts }: { id?: string; testId: string; facts: readonly Fact[] }): ReactNode {
  return (
    <dl id={id} data-testid={testId} className={FACT_LIST_CLS}>
      {facts.map((f) => [
        <dt key={`${f.label}-dt`} className={FACT_LABEL_CLS}>
          {f.label}
        </dt>,
        <dd key={`${f.label}-dd`} className={FACT_VALUE_CLS}>
          {capitalizeFirst(f.value)}
        </dd>,
      ])}
    </dl>
  );
}

export function MapClientSearch({
  pins,
  notPlaced,
  workingStates,
  loading,
  error,
  filtersActive,
  onPick,
  onFocus,
  fullWidth = false,
}: MapClientSearchProps): ReactNode {
  const [text, setText] = useState('');
  const [focusInside, setFocusInside] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();

  const matches = useMemo(() => matchMapClients(pins, notPlaced, text, MAP_CLIENT_SEARCH_LIMIT), [pins, notPlaced, text]);
  const showList = focusInside && text.trim() !== '';
  const nothingMatches = matches.placed.length === 0 && matches.notPlaced.length === 0;

  const choose = (clientId: string): void => {
    onPick(clientId);
    setText('');
    inputRef.current?.focus();
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>): void => {
    const next = e.relatedTarget as Node | null;
    if (next === null || !e.currentTarget.contains(next)) setFocusInside(false);
  };

  const factsId = (clientId: string): string => `${listId}-facts-${clientId}`;

  const row = (clientId: string, name: string, facts: readonly Fact[], muted = false): ReactNode => (
    <li key={clientId} className="px-1.5 py-1" data-muted={muted ? 'true' : undefined}>
      <Button
        variant="ghost"
        size="sm"
        className="w-full !justify-start text-left"
        aria-describedby={factsId(clientId)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => choose(clientId)}
        data-testid={`map-client-search-option-${clientId}`}
      >
        <span className={`min-w-0 break-words ${muted ? MUTED_NAME_CLS : ''}`}>{capitalizeFirst(name)}</span>
      </Button>
      <div className={`px-3.5 pb-1 ${muted ? MUTED_FACTS_CLS : ''}`}>
        <FactList id={factsId(clientId)} testId={`map-client-search-facts-${clientId}`} facts={facts} />
      </div>
    </li>
  );

  return (
    <div
      className={`relative w-full max-w-full ${fullWidth ? '' : 'sm:w-72'}`}
      onFocus={() => setFocusInside(true)}
      onBlur={handleBlur}
    >
      <SearchField
        ref={inputRef}
        value={text}
        onChange={setText}
        aria-label="Find a client on the map"
        placeholder="Find a client"
        testId="map-client-search"
        className={`w-full max-w-full ${fullWidth ? '' : 'sm:w-72'}`}
        aria-controls={showList ? listId : undefined}
        onFocus={() => onFocus?.()}
      />
      {showList && (
        <ul
          id={listId}
          data-testid="map-client-search-results"
          aria-label="Matching clients"
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-card border border-sage-400/25 bg-paper py-1 shadow-sh2"
        >
          {error ? (
            <li role="alert" className="px-3.5 py-1.5 text-xs text-danger" data-testid="map-client-search-error">
              The client list didn't load. Reload the page and search again.
            </li>
          ) : loading ? (
            <li className="px-3.5 py-1.5 text-xs text-ink-soft" data-testid="map-client-search-loading">
              Loading clients.
            </li>
          ) : nothingMatches ? (
            <li className="space-y-1 px-3.5 py-1.5 text-xs text-ink-soft" data-testid="map-client-search-empty">
              {MAP_SEARCH_NO_MATCH_LINES.map((line) => (
                <p key={line}>{line}</p>
              ))}
              {filtersActive && <p data-testid="map-client-search-empty-filter">{MAP_SEARCH_FILTER_LINE}</p>}
            </li>
          ) : (
            <>
              {matches.placed.map((p) =>
                row(p.client_id, p.display_name, [
                  ...(p.cm_name ? [{ label: 'Care manager', value: p.cm_name }] : []),
                  ...(p.unit_name ? [{ label: 'Unit', value: p.unit_name }] : []),
                  ...(p.precision === 'zip' ? [{ label: 'Location', value: 'Approximate, town or ZIP only' }] : []),
                ]),
              )}
              {matches.notPlaced.map((c) =>
                row(
                  c.client_id,
                  c.display_name,
                  [{ label: 'Not on the map', value: clientNotPlacedLabel(c.reason, workingStates) }],
                  c.reason === 'no_address',
                ),
              )}
              {matches.more > 0 && (
                <li className="px-3.5 py-1.5 text-xs text-ink-soft" data-testid="map-client-search-more">
                  {matches.more} more; keep typing to narrow the list.
                </li>
              )}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
